import React, { useState, useCallback, useEffect } from 'react';
import { Upload, X, CheckCircle, FileText, AlertCircle, Loader2, FileCheck, RefreshCw } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const ALLOWED_TYPES = {
  'application/pdf': 'PDF',
  'image/jpeg': 'JPG',
  'image/png': 'PNG',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'DOCX'
};

const InformationStep = ({
  informationFields = [],
  documentRequirements = [],
  collectedInformation,
  setCollectedInformation,
  uploadedDocuments,
  setUploadedDocuments,
  companyId,
  onBack,
  onContinue,
  isValid
}) => {
  const { token } = useAuth();
  const [uploadProgress, setUploadProgress] = useState({});
  const [uploadErrors, setUploadErrors] = useState({});
  const [fieldErrors, setFieldErrors] = useState({});
  
  // Vault documents state
  const [vaultDocuments, setVaultDocuments] = useState([]);
  const [vaultMatches, setVaultMatches] = useState({}); // { doc_key: vaultDoc }
  const [showOverride, setShowOverride] = useState({}); // { doc_key: boolean }
  const [loadingVault, setLoadingVault] = useState(false);

  // Fetch vault documents on mount
  useEffect(() => {
    if (companyId && documentRequirements.length > 0) {
      fetchVaultDocuments();
    }
  }, [companyId, documentRequirements]);

  const fetchVaultDocuments = async () => {
    setLoadingVault(true);
    try {
      const response = await axios.get(`${API}/companies/${companyId}/documents`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const docs = response.data.documents || [];
      setVaultDocuments(docs);
      
      // Match vault documents to requirements
      const matches = {};
      for (const req of documentRequirements) {
        const reqName = req.name.toLowerCase().trim();
        const match = docs.find(vaultDoc => {
          // Match by document_type (case-insensitive)
          const vaultType = (vaultDoc.document_type || '').toLowerCase().trim();
          const vaultName = (vaultDoc.file_name || '').toLowerCase().trim();
          return vaultType === reqName || 
                 vaultType.includes(reqName) || 
                 reqName.includes(vaultType) ||
                 vaultName.includes(reqName);
        });
        if (match && match.is_current) {
          matches[req.doc_key] = match;
        }
      }
      setVaultMatches(matches);
    } catch (error) {
      console.error('Error fetching vault documents:', error);
    } finally {
      setLoadingVault(false);
    }
  };

  // Handle information field change
  const handleFieldChange = (fieldKey, value) => {
    setCollectedInformation(prev => ({
      ...prev,
      [fieldKey]: value
    }));
    // Clear error when user starts typing
    if (fieldErrors[fieldKey]) {
      setFieldErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[fieldKey];
        return newErrors;
      });
    }
  };

  // Validate required fields
  const validateFields = () => {
    const errors = {};
    informationFields.forEach(field => {
      if (field.required) {
        const value = collectedInformation[field.field_key];
        if (value === undefined || value === null || value === '') {
          errors[field.field_key] = `${field.label} is required`;
        }
      }
    });
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle file selection
  const handleFileSelect = async (docKey, file) => {
    // Validate file type
    if (!ALLOWED_TYPES[file.type]) {
      setUploadErrors(prev => ({
        ...prev,
        [docKey]: 'File type not allowed. Allowed: PDF, JPG, PNG, DOCX'
      }));
      return;
    }

    // Validate file size
    if (file.size > MAX_FILE_SIZE) {
      setUploadErrors(prev => ({
        ...prev,
        [docKey]: 'File size exceeds 10MB limit'
      }));
      return;
    }

    // Clear previous errors
    setUploadErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[docKey];
      return newErrors;
    });

    // Start upload
    setUploadProgress(prev => ({ ...prev, [docKey]: 0 }));

    try {
      // Step 1: Get presigned URL (without order_id for pre-order upload)
      const presignedResponse = await axios.post(
        `${API}/upload/presigned`,
        {
          file_name: file.name,
          content_type: file.type
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      const { upload_url, file_url, s3_key } = presignedResponse.data;

      // Step 2: Upload file to S3
      await axios.put(upload_url, file, {
        headers: {
          'Content-Type': file.type
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          setUploadProgress(prev => ({ ...prev, [docKey]: percentCompleted }));
        }
      });

      // Step 3: Store uploaded document info (mark as newly uploaded)
      setUploadedDocuments(prev => ({
        ...prev,
        [docKey]: {
          doc_key: docKey,
          file_name: file.name,
          file_url: file_url,
          s3_key: s3_key,
          file_size: file.size,
          content_type: file.type,
          source: 'upload',
          source_document_id: null
        }
      }));

      // Hide override option after successful upload
      setShowOverride(prev => ({ ...prev, [docKey]: false }));

      // Clear progress after successful upload
      setUploadProgress(prev => {
        const newProgress = { ...prev };
        delete newProgress[docKey];
        return newProgress;
      });

    } catch (error) {
      console.error('Upload error:', error);
      setUploadErrors(prev => ({
        ...prev,
        [docKey]: error.response?.data?.detail || 'Upload failed. Please try again.'
      }));
      setUploadProgress(prev => {
        const newProgress = { ...prev };
        delete newProgress[docKey];
        return newProgress;
      });
    }
  };

  // Handle using vault document
  const handleUseVaultDoc = (docKey, vaultDoc) => {
    setUploadedDocuments(prev => ({
      ...prev,
      [docKey]: {
        doc_key: docKey,
        file_name: vaultDoc.file_name,
        file_url: vaultDoc.file_url,
        s3_key: vaultDoc.s3_key,
        file_size: vaultDoc.file_size,
        content_type: null,
        source: 'vault',
        source_document_id: vaultDoc.id
      }
    }));
  };

  // Handle file removal
  const handleRemoveFile = (docKey) => {
    setUploadedDocuments(prev => {
      const newDocs = { ...prev };
      delete newDocs[docKey];
      return newDocs;
    });
    setShowOverride(prev => ({ ...prev, [docKey]: false }));
  };

  // Handle showing override upload zone
  const handleShowOverride = (docKey) => {
    setShowOverride(prev => ({ ...prev, [docKey]: true }));
  };

  // Handle drag and drop
  const handleDrop = useCallback((docKey) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    const file = e.dataTransfer.files[0];
    if (file) {
      handleFileSelect(docKey, file);
    }
  }, [token]);

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  // Format date for display
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  // Check if document requirement is satisfied
  const isDocSatisfied = (docKey) => {
    // Check if uploaded (either new or from vault)
    if (uploadedDocuments[docKey]) return true;
    // Check if vault match exists and not overriding
    if (vaultMatches[docKey] && !showOverride[docKey]) return true;
    return false;
  };

  // Get document state
  const getDocState = (docKey) => {
    if (uploadedDocuments[docKey]) {
      return uploadedDocuments[docKey].source === 'vault' ? 'vault_match' : 'newly_uploaded';
    }
    if (vaultMatches[docKey] && !showOverride[docKey]) {
      return 'vault_match';
    }
    return 'pending';
  };

  // Render field based on type
  const renderField = (field) => {
    const value = collectedInformation[field.field_key] ?? '';
    const error = fieldErrors[field.field_key];

    const commonClasses = `w-full px-4 py-3 bg-gray-800 border rounded-lg text-white focus:outline-none focus:ring-1 transition-colors ${
      error 
        ? 'border-red-500 focus:border-red-500 focus:ring-red-500' 
        : 'border-gray-700 focus:border-[#059669] focus:ring-[#059669]'
    }`;

    switch (field.field_type) {
      case 'text':
        return (
          <input
            type="text"
            value={value}
            onChange={(e) => handleFieldChange(field.field_key, e.target.value)}
            className={commonClasses}
            placeholder={`Enter ${field.label.toLowerCase()}`}
            data-testid={`field-${field.field_key}`}
          />
        );

      case 'textarea':
        return (
          <textarea
            value={value}
            onChange={(e) => handleFieldChange(field.field_key, e.target.value)}
            className={`${commonClasses} min-h-[100px] resize-y`}
            placeholder={`Enter ${field.label.toLowerCase()}`}
            data-testid={`field-${field.field_key}`}
          />
        );

      case 'dropdown':
        return (
          <select
            value={value}
            onChange={(e) => handleFieldChange(field.field_key, e.target.value)}
            className={commonClasses}
            data-testid={`field-${field.field_key}`}
          >
            <option value="">Select {field.label.toLowerCase()}</option>
            {(field.options || []).map((option, idx) => (
              <option key={idx} value={option}>
                {option}
              </option>
            ))}
          </select>
        );

      case 'date':
        return (
          <input
            type="date"
            value={value}
            onChange={(e) => handleFieldChange(field.field_key, e.target.value)}
            className={commonClasses}
            data-testid={`field-${field.field_key}`}
          />
        );

      case 'boolean':
        return (
          <div className="flex items-center gap-4">
            <button
              type="button"
              onClick={() => handleFieldChange(field.field_key, true)}
              className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                value === true
                  ? 'bg-[#059669] text-gray-900'
                  : 'bg-gray-800 text-gray-400 border border-gray-700 hover:border-gray-600'
              }`}
              data-testid={`field-${field.field_key}-yes`}
            >
              Yes
            </button>
            <button
              type="button"
              onClick={() => handleFieldChange(field.field_key, false)}
              className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                value === false
                  ? 'bg-[#059669] text-gray-900'
                  : 'bg-gray-800 text-gray-400 border border-gray-700 hover:border-gray-600'
              }`}
              data-testid={`field-${field.field_key}-no`}
            >
              No
            </button>
          </div>
        );

      default:
        return (
          <input
            type="text"
            value={value}
            onChange={(e) => handleFieldChange(field.field_key, e.target.value)}
            className={commonClasses}
            placeholder={`Enter ${field.label.toLowerCase()}`}
            data-testid={`field-${field.field_key}`}
          />
        );
    }
  };

  // Render document upload zone
  const renderDocumentUpload = (doc) => {
    const uploaded = uploadedDocuments[doc.doc_key];
    const vaultMatch = vaultMatches[doc.doc_key];
    const progress = uploadProgress[doc.doc_key];
    const error = uploadErrors[doc.doc_key];
    const isUploading = progress !== undefined;
    const docState = getDocState(doc.doc_key);
    const isOverriding = showOverride[doc.doc_key];

    return (
      <div
        key={doc.doc_key}
        className="bg-[#111827] border border-gray-800 rounded-lg p-5"
        data-testid={`doc-upload-${doc.doc_key}`}
      >
        <div className="flex items-start justify-between mb-3">
          <div>
            <h4 className="text-white font-medium flex items-center gap-2">
              {doc.name}
              {doc.required && <span className="text-red-400 text-sm">*</span>}
            </h4>
            {doc.description && (
              <p className="text-gray-400 text-sm mt-1">{doc.description}</p>
            )}
          </div>
          {(uploaded || (docState === 'vault_match' && !isOverriding)) && (
            <button
              onClick={() => handleRemoveFile(doc.doc_key)}
              className="text-gray-400 hover:text-red-400 transition-colors"
              data-testid={`remove-doc-${doc.doc_key}`}
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>

        {/* Vault Match State - Show "Already on file" */}
        {docState === 'vault_match' && !isOverriding && !uploaded && vaultMatch && (
          <div>
            <div className="flex items-center gap-3 p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
              <FileCheck className="w-5 h-5 text-green-500 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-green-400 text-sm font-medium">Already on file</p>
                <p className="text-white text-sm truncate">{vaultMatch.file_name}</p>
                <p className="text-gray-400 text-xs">
                  Uploaded {formatDate(vaultMatch.uploaded_at)}
                </p>
              </div>
            </div>
            <button
              onClick={() => {
                handleUseVaultDoc(doc.doc_key, vaultMatch);
              }}
              className="hidden" // Auto-use vault doc
            />
            <button
              onClick={() => handleShowOverride(doc.doc_key)}
              className="mt-2 text-sm text-gray-400 hover:text-[#059669] flex items-center gap-1 transition-colors"
              data-testid={`override-doc-${doc.doc_key}`}
            >
              <RefreshCw className="w-3 h-3" />
              Upload a different version
            </button>
          </div>
        )}

        {/* Newly Uploaded State */}
        {uploaded && docState === 'newly_uploaded' && (
          <div className="flex items-center gap-3 p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
            <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-white text-sm truncate">{uploaded.file_name}</p>
              <p className="text-gray-400 text-xs">
                {uploaded.file_size ? `${(uploaded.file_size / 1024).toFixed(1)} KB` : 'Uploaded'}
              </p>
            </div>
            <FileText className="w-5 h-5 text-gray-500" />
          </div>
        )}

        {/* Vault Document Used State */}
        {uploaded && docState === 'vault_match' && (
          <div>
            <div className="flex items-center gap-3 p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
              <FileCheck className="w-5 h-5 text-green-500 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-green-400 text-sm font-medium">Using document from vault</p>
                <p className="text-white text-sm truncate">{uploaded.file_name}</p>
              </div>
            </div>
            <button
              onClick={() => handleShowOverride(doc.doc_key)}
              className="mt-2 text-sm text-gray-400 hover:text-[#059669] flex items-center gap-1 transition-colors"
              data-testid={`override-doc-${doc.doc_key}`}
            >
              <RefreshCw className="w-3 h-3" />
              Upload a different version
            </button>
          </div>
        )}

        {/* Uploading State */}
        {isUploading && (
          <div className="p-4">
            <div className="flex items-center gap-3 mb-2">
              <Loader2 className="w-5 h-5 text-[#059669] animate-spin" />
              <span className="text-white text-sm">Uploading... {progress}%</span>
            </div>
            <div className="w-full h-2 bg-gray-800 rounded-full overflow-hidden">
              <div
                className="h-full bg-[#059669] transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        )}

        {/* Upload Zone - Show when no vault match, or when overriding */}
        {docState === 'pending' && !isUploading && (
          <label
            className="block cursor-pointer"
            onDrop={handleDrop(doc.doc_key)}
            onDragOver={handleDragOver}
          >
            <input
              type="file"
              className="hidden"
              accept=".pdf,.jpg,.jpeg,.png,.docx"
              onChange={(e) => {
                const file = e.target.files[0];
                if (file) handleFileSelect(doc.doc_key, file);
              }}
              data-testid={`file-input-${doc.doc_key}`}
            />
            <div className="border-2 border-dashed border-gray-700 rounded-lg p-6 text-center hover:border-gray-600 transition-colors">
              <Upload className="w-8 h-8 text-gray-500 mx-auto mb-2" />
              <p className="text-gray-300 text-sm">
                Click to upload or drag and drop
              </p>
              <p className="text-gray-500 text-xs mt-1">
                PDF, JPG, PNG, DOCX (max 10MB)
              </p>
            </div>
          </label>
        )}

        {/* Override Upload Zone */}
        {isOverriding && !isUploading && !uploaded && (
          <div className="mt-3">
            <label
              className="block cursor-pointer"
              onDrop={handleDrop(doc.doc_key)}
              onDragOver={handleDragOver}
            >
              <input
                type="file"
                className="hidden"
                accept=".pdf,.jpg,.jpeg,.png,.docx"
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) handleFileSelect(doc.doc_key, file);
                }}
                data-testid={`file-input-override-${doc.doc_key}`}
              />
              <div className="border-2 border-dashed border-gray-700 rounded-lg p-6 text-center hover:border-gray-600 transition-colors">
                <Upload className="w-8 h-8 text-gray-500 mx-auto mb-2" />
                <p className="text-gray-300 text-sm">
                  Click to upload or drag and drop
                </p>
                <p className="text-gray-500 text-xs mt-1">
                  PDF, JPG, PNG, DOCX (max 10MB)
                </p>
              </div>
            </label>
            <button
              onClick={() => setShowOverride(prev => ({ ...prev, [doc.doc_key]: false }))}
              className="mt-2 text-sm text-gray-400 hover:text-white transition-colors"
            >
              Cancel
            </button>
          </div>
        )}

        {error && (
          <div className="flex items-center gap-2 mt-3 text-red-400 text-sm">
            <AlertCircle className="w-4 h-4" />
            {error}
          </div>
        )}
      </div>
    );
  };

  // Auto-use vault documents that haven't been manually uploaded
  useEffect(() => {
    // For each vault match, if not already in uploadedDocuments, add it
    Object.entries(vaultMatches).forEach(([docKey, vaultDoc]) => {
      if (!uploadedDocuments[docKey] && !showOverride[docKey]) {
        handleUseVaultDoc(docKey, vaultDoc);
      }
    });
  }, [vaultMatches]);

  return (
    <div className="space-y-8">
      {/* Section A: Information Fields */}
      {informationFields.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold text-white mb-6">
            Required Information
          </h2>
          <div className="space-y-5">
            {informationFields
              .sort((a, b) => (a.order || 0) - (b.order || 0))
              .map((field) => (
                <div key={field.field_key}>
                  <label className="block text-gray-300 text-sm font-medium mb-2">
                    {field.label}
                    {field.required && <span className="text-red-400 ml-1">*</span>}
                  </label>
                  {renderField(field)}
                  {fieldErrors[field.field_key] && (
                    <p className="text-red-400 text-sm mt-1 flex items-center gap-1">
                      <AlertCircle className="w-4 h-4" />
                      {fieldErrors[field.field_key]}
                    </p>
                  )}
                </div>
              ))}
          </div>
        </div>
      )}

      {/* Section B: Document Uploads */}
      {documentRequirements.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold text-white mb-2">
            Required Documents
          </h2>
          {loadingVault ? (
            <div className="flex items-center gap-2 text-gray-400 text-sm mb-4">
              <Loader2 className="w-4 h-4 animate-spin" />
              Checking your document vault...
            </div>
          ) : Object.keys(vaultMatches).length > 0 && (
            <p className="text-gray-400 text-sm mb-4">
              Some documents are already on file from your company vault.
            </p>
          )}
          <div className="space-y-4">
            {documentRequirements.map((doc) => renderDocumentUpload(doc))}
          </div>
        </div>
      )}

      {/* Navigation */}
      <div className="flex justify-between pt-4">
        <button
          onClick={onBack}
          className="flex items-center gap-2 px-6 py-3 rounded-lg font-medium text-gray-300 bg-gray-800 hover:bg-gray-700 transition-colors"
          data-testid="step-details-back-btn"
        >
          Back
        </button>
        <button
          onClick={() => {
            if (validateFields()) {
              onContinue();
            }
          }}
          disabled={!isValid}
          className="flex items-center gap-2 px-6 py-3 rounded-lg font-medium text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          style={{ backgroundColor: isValid ? '#059669' : '#4B5563' }}
          data-testid="step-details-continue-btn"
        >
          Continue
        </button>
      </div>
    </div>
  );
};

export default InformationStep;
